import { useState, useEffect, useCallback } from 'react';
import { isBrowser } from 'ws-scripts/modules/environment';
import logger from 'ws-scripts/modules/logger';
import { enhanceLocationsWithCoordinates } from '../utils/locationCoordinateEnhancer';
import getDisplayLocations from '../utils/getDisplayLocations';

const LOG_PREFIX = '[ws-locations-search][useLocationsData]';
const POLL_INTERVAL_MS = 100;

/**
 * Hook to integrate with ws-locations-data widget.
 */
export const useLocationsData = () => {
	const [locations, setLocations] = useState([]);
	const [error, setError] = useState(null);
	const [isReady, setIsReady] = useState(false);
	const [isLoading, setIsLoading] = useState(true);

	// Check if LocationsData is available (polling + event listener)
	useEffect(() => {
		if (!isBrowser) {
			return () => {};
		}

		const checkReady = () => {
			const hasLocationData = window.DDC?.LocationsData;

			if (hasLocationData) {
				setIsReady(true);
				return true;
			}
			return false;
		};

		// Check immediately
		if (checkReady()) {
			return () => {};
		}

		// Listen for the DDC:LocationsDataReady custom event
		const handleReadyEvent = () => {
			checkReady();
		};
		window.addEventListener('DDC:LocationsDataReady', handleReadyEvent);

		// Keep polling as fallback until LocationsData is available
		const pollInterval = setInterval(() => {
			if (checkReady()) {
				clearInterval(pollInterval);
			}
		}, POLL_INTERVAL_MS);

		// Cleanup
		return () => {
			clearInterval(pollInterval);
			window.removeEventListener(
				'DDC:LocationsDataReady',
				handleReadyEvent
			);
		};
	}, []);

	// Subscribe to location updates and filtered-location updates
	useEffect(() => {
		if (!isReady || !window.DDC?.LocationsData) {
			return () => {};
		}

		const locationsData = window.DDC.LocationsData;

		// Counter to detect stale async results. Every call to
		// refreshLocations increments this; when the async coordinate
		// enhancement resolves it checks whether its captured id still
		// matches. If a newer update arrived in the meantime the result
		// is silently discarded, preventing the race condition where a
		// slow geocode response overwrites a more-recent locations state.
		let updateId = 0;

		const refreshLocations = async (reason) => {
			updateId += 1;
			const myUpdateId = updateId;

			const displayLocations = getDisplayLocations(locationsData);

			// Synchronous state updates — always applied immediately
			setLocations(displayLocations);

			if (reason === 'locations') {
				setError(locationsData.getError());
				setIsLoading(false);
			}

			if (reason === 'filtered') {
				logger.debug(`${LOG_PREFIX} Filter update received`, {
					displayCount: displayLocations.length
				});
			}

			// Async coordinate enhancement — guarded by staleness check
			try {
				const { enriched, hasNewCoordinates } =
					await enhanceLocationsWithCoordinates(displayLocations);

				if (hasNewCoordinates && myUpdateId === updateId) {
					setLocations(enriched);
				}
			} catch (coordinateError) {
				logger.warn(
					`${LOG_PREFIX} Failed to enhance locations with coordinates:`,
					coordinateError
				);
			}
		};

		// General location updates (search-param changes, initial load)
		const handleLocationsUpdate = () => refreshLocations('locations');

		const unsubscribeLocations =
			locationsData.registerLocationsNotification(handleLocationsUpdate);

		let unsubscribeFiltered;
		if (
			typeof locationsData.registerFilteredLocationsNotification ===
			'function'
		) {
			const handleFilteredUpdate = () => refreshLocations('filtered');

			unsubscribeFiltered =
				locationsData.registerFilteredLocationsNotification(
					handleFilteredUpdate
				);
		} else {
			logger.debug(
				`${LOG_PREFIX} registerFilteredLocationsNotification not available - skipping filter subscription`
			);
		}

		// Cleanup both subscriptions on unmount

		return () => {
			// Invalidate any in-flight async enhancement so it won't
			// call setLocations after subscriptions are torn down.
			updateId += 1;

			if (unsubscribeLocations) {
				unsubscribeLocations();
			}
			if (unsubscribeFiltered) {
				unsubscribeFiltered();
			}
		};
	}, [isReady]);

	// Function to trigger location search via ws-locations-data
	const setSearchParams = useCallback(
		(postalCode, range, excludeByRange) => {
			if (!isReady || !window.DDC?.LocationsData) {
				logger.warn(
					`${LOG_PREFIX} Cannot set search params - LocationsData not ready`
				);
				return;
			}

			setIsLoading(true);
			setError(null);
			window.DDC.LocationsData.setSearchParams(
				postalCode,
				range,
				excludeByRange
			);
		},
		[isReady]
	);

	return {
		locations,
		locationCount: locations.length,
		error,
		isReady,
		isLoading,
		setIsLoading,
		setSearchParams
	};
};
